// R3 Services — the explicit, static application configuration for R3.
//
// This file is the ONLY place R3's applications are described. Components never branch on "r3"; they read
// these definitions through applications/applicationCatalog.js, whose config source is meant to be replaced
// by the shared company registry API later (see applications/README.md for the contract).
//
// Rules (asserted in applicationCatalog.test.js):
//   • Pure data. No React, no window, no process.env reads — env is injected into buildR3Company(env).
//   • Nothing secret and no key VALUES. The booking page's public flow key is exposed only through
//     runtimeKeys (consumed by routing), never inside an application definition that a screen renders.
//   • Routes are the real existing /r3* routes.

// R3 ride-share Cognito groups (pool us-east-1_0rCESwCjy devl / us-east-1_ZfaQEhpEA prod).
// Both conventions are accepted so no existing account is locked out (R3GROUPS-2):
//   surface       new (preferred)   legacy (still accepted)
//   /r3ops        r3_admin          admin
//   /r3dispatch   r3_dispatcher     operations, admin
//   /r3driver     r3_driver         driver
// The legacy `admin` group is an R3 COMPANY administrator — never a Movorra platform super-admin.
export const R3_ADMIN_GROUPS = ['r3_admin', 'admin'];
export const R3_DISPATCH_GROUPS = ['r3_dispatcher', 'r3_admin', 'operations', 'admin'];
export const R3_DRIVER_GROUPS = ['r3_driver', 'driver'];
// Everyone on the R3 side, for the customer chat rows (CHATBOUNCE-1): R3 staff must never be bounced off R3's own
// customer pages. Both chats are public now (R3-CHAT-CARD-1), so this only matters to group checks such as
// landing/login-destination. The chat never uses the session.
export const R3_CUSTOMER_GROUPS = ['customer', ...R3_ADMIN_GROUPS, ...R3_DISPATCH_GROUPS, ...R3_DRIVER_GROUPS];

export const R3_AGENT_TEAM = 'travel_desk_manager';
// The Studio flow the public booking page runs. Informational in the frontend: the backend resolves the flow
// from the public key, not from this id.
export const R3_BOOKING_FLOW_ID = 'r3-ride-booking';

const BRANDING = { companyName: 'R3 Services', subtitle: 'BOOK A RIDE', logoSrc: null, isCharter: false };

export function buildR3Company(env = {}) {
  const bookingKey = String(env.REACT_APP_R3_ECAP_FLOW_KEY || '').trim();
  return {
    tenantId: 'r3',
    displayName: 'R3 Services',
    branding: BRANDING,
    // COMPANY-STUDIO-1: an R3 administrator signs in at /studio with the company login and sees only these Studio
    // screens (movorra-flows accepts r3_admin for tenant r3 on devl). Dispatchers and drivers who sign in at /studio
    // are sent to their own app.
    studio: { adminGroups: ['r3_admin'], screens: ['ops-applications', 'ops-integration'] },
    applications: [
      {
        appId: 'r3-ops',
        type: 'operations',
        route: '/r3ops',
        aliases: ['/adminops'],
        displayName: 'Operations Dashboard',
        description: 'Manage bookings, drivers, dispatchers, and daily operations.',
        icon: 'navigate',
        access: { public: false, identityPool: 'rideshare', anyOfGroups: R3_ADMIN_GROUPS },
        runtime: { engine: 'movorra-api' },
        capabilities: {},
        records: { studio: false },
        status: { configured: true },
      },
      {
        appId: 'r3-dispatch',
        type: 'dispatch',
        route: '/r3dispatch',
        aliases: ['/dispatchops'],
        displayName: 'Dispatch Dashboard',
        description: 'Assign, coordinate, and monitor rides.',
        icon: 'route',
        access: { public: false, identityPool: 'rideshare', anyOfGroups: R3_DISPATCH_GROUPS },
        runtime: { engine: 'movorra-api' },
        capabilities: {},
        records: { studio: false },
        status: { configured: true },
      },
      {
        appId: 'r3-driver',
        type: 'driver',
        route: '/r3driver',
        aliases: ['/driverops'],
        displayName: 'Driver Application',
        description: 'Driver-facing application for assigned rides.',
        icon: 'car',
        access: { public: false, identityPool: 'rideshare', anyOfGroups: R3_DRIVER_GROUPS },
        runtime: { engine: 'movorra-api' },
        capabilities: {},
        records: { studio: false },
        status: { configured: true },
      },
      {
        appId: 'r3-booking',
        type: 'booking-chat',
        // Hidden from the Applications page (R3-CHAT-CARD-1): R3's customer booking card is the agents chat
        // (/r3chat) for now. The /r3ride route itself still works, and still feeds Studio Leads/Analytics.
        listed: false,
        route: '/r3ride',
        aliases: [],
        displayName: 'Studio Booking Flow',
        description: 'Public booking chat running the Studio flow. Its conversations appear in Studio Leads and Analytics.',
        icon: 'sparkles',
        access: { public: true, identityPool: 'rideshare', anyOfGroups: R3_CUSTOMER_GROUPS },
        runtime: { engine: 'ecap-flow', flowId: R3_BOOKING_FLOW_ID, publicKeyConfigured: !!bookingKey },
        // Verified against templates/r3/r3-ride-booking.flow.json: the flow has no human://approve and no
        // human://handoff action, so it creates no Approvals and no Live conversations. Flip these only
        // after R3 decides on those steps AND the flow actually contains them.
        capabilities: { leads: true, analytics: true, approvals: false, liveHandoff: false },
        records: { studio: true, source: '/r3ride' },
        status: bookingKey
          ? { configured: true }
          : { configured: false, reason: 'No public booking key is configured for this environment.' },
      },
      {
        appId: 'r3-agent-chat',
        type: 'agent-chat',
        // R3-CHAT-CARD-1: R3's listed customer booking app, and public — it opens with no login on every host,
        // devl included (the agents chat routes skip JWT, so the backend never needed the login).
        route: '/r3chat',
        aliases: ['/chat', '/webchat'],
        displayName: 'Customer Booking',
        description: 'Public booking chat where customers get a quote and book a ride. Runs on the agents service, so its conversations are not recorded in Studio.',
        icon: 'sparkles',
        access: { public: true, identityPool: 'rideshare', anyOfGroups: R3_CUSTOMER_GROUPS },
        runtime: { engine: 'agents', teamId: R3_AGENT_TEAM, agentId: '' },
        capabilities: {},
        records: { studio: false },
        status: { configured: true },
      },
    ],
    // Values routing needs but screens must never render.
    runtimeKeys: { 'r3-booking': bookingKey },
  };
}
